import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  addStaffEmail,
  changePersonRole,
  loadPeople,
  removePerson,
  type PeopleState,
  type SignedInUser,
} from '../api';

export function PeoplePage() {
  const [state, setState] = useState<PeopleState | { kind: 'loading' }>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadPeople().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function reload(): Promise<void> {
    setState(await loadPeople());
  }

  async function addPerson(event: { preventDefault(): void }): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    const error = await addStaffEmail(email);
    setBusy(false);
    if (error) {
      setMessage(error);
      return;
    }
    setEmail('');
    await reload();
  }

  async function setRole(person: SignedInUser, role: 'admin' | 'staff'): Promise<void> {
    setBusy(true);
    setMessage('');
    const error = await changePersonRole(person.id, role);
    setBusy(false);
    if (error) {
      setMessage(error);
      return;
    }
    await reload();
  }

  async function remove(person: SignedInUser): Promise<void> {
    if (!window.confirm(`Remove ${person.email}?`)) return;
    setBusy(true);
    setMessage('');
    const error = await removePerson(person.id);
    setBusy(false);
    if (error) {
      setMessage(error);
      return;
    }
    await reload();
  }

  return (
    <main className="page">
      <h1>People</h1>
      <p>
        <Link to="/admin">Back to Admin</Link>
      </p>
      {state.kind === 'loading' ? <p>Loading people…</p> : null}
      {state.kind === 'signed-out' ? (
        <p>
          <Link to="/admin">Sign in</Link> before managing people.
        </p>
      ) : null}
      {state.kind === 'forbidden' ? <p className="note">Only an Admin can manage people.</p> : null}
      {state.kind === 'error' ? <p className="note">People could not be loaded.</p> : null}
      {state.kind === 'ready' ? (
        <>
          <form onSubmit={(event) => void addPerson(event)}>
            <label htmlFor="staff-email">Email</label>
            <input
              id="staff-email"
              type="email"
              autoComplete="email"
              maxLength={254}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <button className="button" type="submit" disabled={busy}>
              Add as Staff
            </button>
          </form>
          {message ? <p className="note">{message}</p> : null}
          <table className="people">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.users.map((person) => (
                <tr key={person.id}>
                  <td>{person.email}</td>
                  <td>{person.name}</td>
                  <td>{person.role === 'admin' ? 'Admin' : 'Staff'}</td>
                  <td className="actions">
                    {person.role === 'staff' ? (
                      <button className="button" type="button" disabled={busy} onClick={() => void setRole(person, 'admin')}>
                        Make Admin
                      </button>
                    ) : (
                      <button className="button" type="button" disabled={busy} onClick={() => void setRole(person, 'staff')}>
                        Make Staff
                      </button>
                    )}
                    <button className="button" type="button" disabled={busy} onClick={() => void remove(person)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </main>
  );
}
